import type { StepFailure } from "../../lib/stepTestFailures";
import type { Row } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import {
  type KeyboardEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildFileMap } from "../../../shared/buildFileMap";
import { branchNameError } from "../../../shared/branchName";
import { createZip } from "../../../shared/zip";
import {
  supportsManualGitWebhook,
  supportsPullRequests,
} from "../../../shared/connectors/gitSyncCapabilities";
import type { GitPullRequestState } from "../../../shared/gitPullRequestState";
import { sharedMutators } from "../../../shared/mutators";
import { GIT_SYNC_STALE_MS } from "../../../shared/mutators/syncGit";
import { GIT_SYNC_PAUSED_MESSAGE } from "../../../shared/gitSyncRemote";
import { queries } from "../../../shared/queries";
import { useDeadlinePassed } from "../../hooks/useDeadlinePassed";
import { useDeleteLastBranch } from "../../hooks/useDeleteLastBranch";
import { useGitSyncErrorToast } from "../../hooks/useGitSyncErrorToast";
import { useForkAsBranch } from "../../hooks/useForkAsBranch";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useIsGitSynced } from "../../hooks/useIsGitSynced";
import { useMainMergeStatus } from "../../hooks/useMainMergeStatus";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { usePushLive } from "../../hooks/usePushLive";
import type { usePushLiveRequest } from "../../hooks/usePushLiveRequest";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import {
  BRANCH_INACTIVITY_DAYS,
  branchLastChatActivity,
  isBranchStale,
} from "../../lib/branch";
import { branchRole, type BranchRole } from "../../../shared/branchRole";
import { downloadBlob, downloadChatAsJson } from "../../lib/downloadChat";
import { chatExportPath } from "../../lib/apiPaths";
import { playClick, playPushLive } from "../../lib/sounds";
import { useMakeUrl, navigate, routes } from "../../lib/urls";
import { toSentence } from "../../lib/toSentence";
import { userDisplayName } from "../../lib/userDisplayName";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { Avatar } from "../Avatar";
import { BranchBanner } from "../BranchBanner";
import { ClickButton } from "../ClickButton";
import { ConfirmModal } from "../ConfirmModal";
import { FacePile } from "../FacePile";
import { Icon, type IconName } from "../Icon";
import { BadgeDot } from "../BadgeDot";
import { isInAppNavigation, Link } from "../Link";
import { RefreshSpin } from "../RefreshSpin";
import { RenameModal } from "../RenameModal";
import { LiveIndicator } from "../LiveIndicator";
import { MenuItem } from "../MenuItem";
import { PressButton } from "../PressButton";
import { StatusIndicator } from "../StatusIndicator";
import { Spinner } from "../Spinner";
import { StreamingText } from "../StreamingText";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { PushLivePreflight } from "../PushLivePreflight";
import { Heading, SpaceItemContainer, StyledLink } from "../Navigation";
import { NavigationTreeRow } from "../NavigationTreeRow";
import { NavigationStatus } from "../NavigationStatus";
import { SubtleButton, SubtleButtonLink } from "../SubtleButton";
import { CreatePullRequest } from "./CreatePullRequest";
import { CreateBranchDropdown } from "./CreateBranchDropdown";
import { PushRequestPicker } from "./PushRequestPicker";
import { useToast } from "../Toast";
import { useWorkflowChangeReview } from "../../hooks/useWorkflowChangeReview";
import { Tooltip } from "../Tooltip";
import { TidyUpModal } from "../TidyUpModal";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import type { BranchData, BranchSummary, WorkflowData } from "../Workflow";

export type ChatBannerParts = {
  icon: ReactNode;
  name: string;
  metadata: string;
  controls: ReactNode;
  avatar: ReactNode;
  historyMenu: ReactNode;
  history: { open: boolean; onToggle: () => void };
  anchor: {
    ref: (element: HTMLDivElement | null) => void;
    anchorName: string;
    onKeyDown: KeyboardEventHandler<HTMLDivElement>;
  };
};

type Props = {
  // A chat hosts the banner and the navigation separately, and each version opens in its own chat.
  chatBanner?: boolean;
  children?: (
    navigation: ReactNode,
    banner: ReactNode,
    chatBanner: ChatBannerParts
  ) => ReactNode;
  workflow: Pick<
    WorkflowData,
    "id" | "name" | "autofixBranchId" | "autoImproveBranchId" | "pausedAt"
  >;
  currentBranch: BranchData;
  branchSummaries: readonly BranchSummary[];
  commitId: string | null;
  spaceId: string;
  presentBranchesByUser: Map<string, Set<string>>;
  hasChangesToPush: boolean;
  canWrite: boolean;
  canCreateBranch: boolean;
  canPushLive: boolean;
  blockedBySelfMerge: boolean;
  pushLiveRequest: ReturnType<typeof usePushLiveRequest>;
  isDeletedOnBranch: boolean;
  isDeploying: boolean;
  isMerging: boolean;
  onDeploying: (v: boolean) => void;
  onMerging: (v: boolean) => void;
  onInvestigateFailures: (failures: readonly StepFailure[]) => void;
};

function getContributors(branch: BranchSummary | BranchData): Row["user"][] {
  return branch.contributors.flatMap((c) => (c.user ? [c.user] : []));
}

async function downloadCodeAsZip(
  z: Parameters<typeof buildFileMap>[0],
  commitId: string,
  workflowName: string
) {
  const fileMap = await buildFileMap(z, commitId);
  const safeName = workflowName.replace(/[/\\]/g, "-");
  const zipFiles: Record<string, string> = {};
  for (const [path, content] of Object.entries(fileMap)) {
    zipFiles[`${safeName}${path}`] = content;
  }
  downloadBlob(createZip(zipFiles), `${safeName}.zip`);
}

type OpenPopover = "none" | "dropdown" | "actions";

function formatVersionDate(timestamp: number): {
  prefix: string;
  date: string;
} {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) {
    return { prefix: "", date: `today, ${timeStr}` };
  }

  return {
    prefix: "",
    date: `${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}, ${timeStr}`,
  };
}

const Wrapper = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

// Room for the row’s menu button is made only while that button can show.
const BranchNavigationRow = styled(SpaceItemContainer)`
  & > a {
    padding-right: 8px;
  }
  &:hover > a,
  &:focus-within > a,
  &[data-menu-open="true"] > a,
  &[data-status="true"] > a {
    padding-right: 32px;
  }
  &:hover > a,
  &:focus-within > a,
  &[data-selected="true"] > a {
    background: var(--alpha4);
  }
  &:focus-within [data-space-action],
  &[data-menu-open="true"] [data-space-action] {
    opacity: 1;
    pointer-events: auto;
  }
  &:focus-within [data-hide-on-hover],
  &[data-menu-open="true"] [data-hide-on-hover] {
    opacity: 0;
  }
  @media (hover: none) {
    & > a {
      padding-right: 32px;
    }
    [data-space-action] {
      opacity: 1;
      pointer-events: auto;
    }
    [data-hide-on-hover] {
      opacity: 0;
    }
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const HistoryIndicator = styled("div")`
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--orange-400);
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--orange-600);
    animation: ${pulse} 2000ms ease-in-out infinite;
  }
`;

const ViewingBadge = styled("span")`
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: var(--sky-400);
  color: var(--sky-1000);
`;

const MenuDivider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

const TidyButton = styled(PressButton)`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-secondary);
  flex-shrink: 0;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:active:not(:disabled) {
    background: var(--alpha8);
  }
`;

const pushLiveIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const pillStyle = `
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border: none;
  outline: none;
  background: var(--green-600);
  border-radius: 38px;
  color: white;
  cursor: pointer;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} var(--motion-fast) var(--ease-out);
`;

const PushLiveButton = styled(ClickButton)`
  ${pillStyle}
  &:hover {
    opacity: 0.8;
  }
  &&:disabled {
    opacity: 0.5;
  }
`;

const PushRequestPill = styled("span")`
  ${pillStyle}
  background: var(--sky-600);
  button:hover > &,
  [aria-expanded="true"] > & {
    opacity: 0.8;
  }
  button:disabled > & {
    opacity: 0.5;
  }
`;

const RequestSentButton = styled(PushLiveButton)`
  background: var(--gray-200);
  &:hover {
    ${DefaultIcon} {
      opacity: 0;
      transform: rotate(90deg) scale(0.5);
    }
    ${HoverIcon} {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }
`;

const ResolveConflictsButton = styled(PushLiveButton)`
  background: var(--orange-600);
`;

const SyncButton = styled(ClickButton)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  background: var(--green-600);
  border-radius: 38px;
  color: white;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} var(--motion-fast) var(--ease-out);
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

const PrBadge = styled("a")`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 38px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const SyncingBadge = styled("div")`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 38px;
  background: var(--alpha4);
  color: var(--text-secondary);
  cursor: default;
  flex-shrink: 0;
`;

// 3B's own status palette: sky for an in-review request, purple (3B's accent)
// once it merges, gray for a resting draft, red for a discarded (closed)
// request.
const PR_STATE_STYLE: Record<
  GitPullRequestState,
  { background: string; color: string }
> = {
  open: { background: "var(--sky-400)", color: "var(--sky-1000)" },
  draft: { background: "var(--gray-400)", color: "var(--gray-1000)" },
  merged: { background: "var(--purple-400)", color: "var(--purple-1000)" },
  closed: { background: "var(--red-400)", color: "var(--red-1000)" },
};

// The status also reads through the icon (dashed = draft, merge glyph = merged,
// slashed = closed) and the tooltip word, so the badge text can stay "PR"/"MR".
const PR_STATE_ICON: Record<GitPullRequestState, IconName> = {
  open: "git-branch",
  draft: "circle-dashed",
  merged: "merge",
  closed: "circle-slash",
};

const PR_STATE_LABEL: Record<GitPullRequestState, string> = {
  open: "Open",
  draft: "Draft",
  merged: "Merged",
  closed: "Closed",
};

const PILL_TOOLTIP_STYLE = { maxWidth: 200 };

const BRANCH_ROLE_ICON: Record<BranchRole, IconName> = {
  autofix: "radar",
  autotune: "audio-waveform",
  other: "git-branch",
};

const ResumeButton = styled(ClickButton)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border: none;
  outline: none;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-primary);
  cursor: pointer;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} var(--motion-fast) var(--ease-out);
  &:hover {
    background: var(--alpha8);
  }
  &&:disabled {
    opacity: 0.5;
  }
`;

const DropdownPopover = styled(AnchoredPopover)`
  top: anchor(bottom);
  left: anchor(left);
  right: anchor(right);
  margin-top: 4px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  padding: 4px;
  z-index: ${zIndexes.branchSelectorDropdown};
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// Right inset is 4px (vs 8px left) so the 24px action button centers over the
// 16px avatar column in the rows below, rather than sitting flush-right.
const DropdownHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  height: 32px;
  padding: 0 4px 0 8px;
  color: var(--text-secondary);
`;

const versionRowStyles = `
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 0;
  outline: 0;
  text-decoration: none;
  color: inherit;
  background: transparent;
  &:hover, &[data-selected="true"] {
    background: var(--alpha4);
  }
`;

const VersionRow = styled("button")`
  ${versionRowStyles}
`;

const VersionRowLink = styled(Link)`
  ${versionRowStyles}
`;

const VersionRowContent = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  flex-wrap: nowrap;
  & > *:not(:first-child) {
    flex-shrink: 0;
  }
`;

const ActionPopover = styled(AnchoredPopover)`
  top: anchor(top);
  left: anchor(right);
  margin-left: 8px;
  position-try-fallbacks:
    flip-inline,
    flip-block,
    flip-inline flip-block,
    --dropdown-fit-viewport,
    --dropdown-fit-viewport flip-block;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  padding: 4px;
  z-index: ${zIndexes.branchSelectorActions};
  min-width: 160px;
`;

// In a chat, opening another version’s actions moves to its chat, which mounts a new selector there.
const actionsOnArrival: { branchId: string | null } = { branchId: null };

export function BranchSelector({
  chatBanner = false,
  children,
  workflow,
  currentBranch,
  branchSummaries,
  commitId,
  spaceId,
  presentBranchesByUser: presentUserBranches,
  hasChangesToPush,
  canWrite,
  canCreateBranch,
  canPushLive,
  blockedBySelfMerge,
  pushLiveRequest,
  isDeletedOnBranch,
  isDeploying,
  isMerging,
  onDeploying,
  onMerging,
  onInvestigateFailures,
}: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const changeReview = useWorkflowChangeReview({
    workflowId: workflow.id,
    commitId,
  });
  const forkAsBranch = useForkAsBranch(spaceId, workflow.id);
  const isGitSynced = useIsGitSynced(spaceId);
  const [gitRemote] = useQuery(queries.loadSpaceGitRemote({ spaceId }));
  const [branchPrs] = useQuery(
    queries.pullRequestsForBranch({ workflowBranchId: currentBranch.id })
  );
  // An active PR wins; otherwise show the most recent terminal one so a merged
  // or closed branch reads as such instead of reoffering "Create PR".
  const branchPr =
    branchPrs?.find((pr) => pr.state === "open" || pr.state === "draft") ??
    branchPrs?.[0];
  const [spaceGitBranch] = useQuery(
    queries.gitBranchForWorkflowBranch({ workflowBranchId: currentBranch.id })
  );
  const hasChangesToSync =
    currentBranch.headCommitId !==
    (spaceGitBranch?.members?.syncedCommitId ?? null);
  const canManageSpace = useHasAccess("space:manage", spaceId);
  const canSync =
    (canPushLive || canManageSpace) &&
    (!commitId || commitId === currentBranch.headCommitId);
  const connectorMissing = gitRemote != null && gitRemote.appUsage == null;
  const syncStale = useDeadlinePassed(
    gitRemote?.syncStartedAt != null
      ? gitRemote.syncStartedAt + GIT_SYNC_STALE_MS
      : null
  );
  const isSyncing = gitRemote?.syncStartedAt != null && !syncStale;
  const syncError = gitRemote?.lastError ?? null;
  // Without a delivery webhook we never learn the PR was opened, so the
  // create link would dangle forever; manual-webhook providers must have one
  // configured before we offer it, and a plain git server has no PRs at all.
  const remoteConnector = gitRemote?.appUsage?.connectedApp;
  const prLinkReady =
    remoteConnector != null &&
    supportsPullRequests(remoteConnector.identifier) &&
    (!supportsManualGitWebhook(remoteConnector.identifier) ||
      gitRemote?.webhookConfiguredAt != null ||
      gitRemote?.webhookReceivedAt != null);
  const { showToast, hideToast } = useToast();
  useGitSyncErrorToast(isSyncing, syncError);
  const [openPopover, setOpenPopover] = useState<OpenPopover>(() =>
    actionsOnArrival.branchId === currentBranch.id ? "actions" : "none"
  );
  useEffect(() => {
    actionsOnArrival.branchId = null;
  }, []);
  const [actionBranchId, setActionBranchId] = useState(currentBranch.id);
  const actionsOpen =
    openPopover === "actions" && actionBranchId === currentBranch.id;
  const [popoverAnchorName] = useState(() => `--version-selector-${nanoid()}`);
  const [navigationAnchorName] = useState(
    () => `--branch-navigation-${nanoid()}`
  );
  const [bannerAnchorName] = useState(() => `--branch-banner-${nanoid()}`);
  const [bannerElement, setBannerElement] = useState<HTMLDivElement | null>(
    null
  );
  const [actionAnchorName] = useState(() => `--action-menu-${nanoid()}`);

  const [containerRef, popoverRef] = useMouseDownOutside(
    () => setOpenPopover("none"),
    bannerElement ?? undefined
  );
  const currentBranchId = currentBranch.id;

  // Sort branches: main first, then other branches by most recent activity
  const [workflowPushRequests] = useQuery(
    queries.pushLiveRequestsForWorkflow({ workflowId: workflow.id })
  );
  const pushRequestDotColor = (branch: BranchSummary) => {
    const request = workflowPushRequests.find((r) => r.branchId === branch.id);
    if (request?.commitId !== branch.headCommitId) return null;
    return request.approvedByUserId ? "var(--green-600)" : "var(--sky-600)";
  };
  const sortedBranches = useMemo(() => {
    const main = branchSummaries.find((b) => b.name === "main");
    const otherBranches = branchSummaries
      .filter((b) => b.name !== "main")
      .sort((a, b) => branchLastChatActivity(b) - branchLastChatActivity(a));
    return [...(main ? [main] : []), ...otherBranches];
  }, [branchSummaries]);

  const isMainBranch = currentBranch.name === "main";
  const currentBranchRole = branchRole({ id: currentBranch.id, workflow });
  const mainBranch = branchSummaries.find((b) => b.name === "main");

  // Only offer non-main branches, and only when a live "main" exists — otherwise
  // tidying the last remaining branch would cascade-delete the workflow.
  const staleBranches = mainBranch
    ? branchSummaries.filter((b) => b.name !== "main" && isBranchStale(b))
    : [];

  const [downloadingCode, setDownloadingCode] = useState(false);
  const [renamingBranch, setRenamingBranch] = useState<Pick<
    BranchData,
    "id" | "name"
  > | null>(null);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const deleteLastBranch = useDeleteLastBranch(canWrite);
  const isPaused = workflow.pausedAt != null;
  if (cleanupModalOpen && (!canWrite || staleBranches.length === 0))
    setCleanupModalOpen(false);
  if (pauseConfirmOpen && (!canWrite || !canPushLive || isPaused))
    setPauseConfirmOpen(false);

  const handleConfirmPause = () => {
    if (!canWrite || !canPushLive) return;
    setPauseConfirmOpen(false);
    setOpenPopover("none");
    z.mutate(sharedMutators.pauseWorkflow({ workflowId: workflow.id }));
  };

  const handleResume = () => {
    if (!canWrite || !canPushLive) return;
    setOpenPopover("none");
    playPushLive();
    z.mutate(sharedMutators.unpauseWorkflow({ workflowId: workflow.id }));
  };

  useEffect(() => {
    if (!downloadingCode) return;
    const targetCommitId = commitId || currentBranch.headCommitId;
    if (!targetCommitId) {
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: clears the downloading flag when there is no commit to feed the async zip download
      setDownloadingCode(false);
      return;
    }
    void downloadCodeAsZip(z, targetCommitId, workflow.name).finally(() =>
      setDownloadingCode(false)
    );
  }, [downloadingCode, commitId, currentBranch.headCommitId, workflow.name, z]);

  const getBranchUrl = useCallback(
    (branchId: string) =>
      makeUrl(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId,
        commitId: "",
      }),
    [spaceId, workflow.id, makeUrl]
  );

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && openPopover !== "none") {
      e.preventDefault();
      setOpenPopover("none");
    }
  };

  // Get checkpoint info
  const checkpoints = currentBranch.commits.filter((c) => c.message);
  const versionNumber = checkpoints.length;
  const isViewingHistory =
    commitId &&
    commitId !== currentBranch.headCommitId &&
    checkpoints.some((c) => c.id === commitId);

  // Get display info for current branch
  const getDisplayName = (branch: {
    id: string;
    name: string;
    commits?: readonly { message: string | null }[];
  }): ReactNode => {
    if (branch.name === "main") {
      const checkpointCount =
        branch.commits?.filter((c) => c.message).length ?? 0;
      return `V${checkpointCount}`;
    }
    return <StreamingText text={branch.name} branchId={branch.id} />;
  };

  const getTimestampInfo = (branch: {
    name: string;
    createdAt: number;
    lastChatEntryAt: number | null;
  }): { prefix: string; date: string } => {
    const timestamp = branch.lastChatEntryAt ?? branch.createdAt;
    const { date } = formatVersionDate(timestamp);
    const prefix = branch.name === "main" ? "pushed live" : "edited";
    return { prefix, date };
  };

  const isLastBranch = branchSummaries.length <= 1;

  const handleDeleteBranch = () => {
    if (!canWrite) return;
    if (isLastBranch) {
      setOpenPopover("none");
      deleteLastBranch.confirm(currentBranch.id, spaceId);
      return;
    }
    z.mutate(sharedMutators.deleteBranch({ branchId: currentBranch.id }));
    navigate(routes.space.workflow, {
      spaceId,
      workflowId: workflow.id,
      branchId: mainBranch?.id ?? "",
    });
    setOpenPopover("none");
  };

  const handleTidyUp = () => {
    if (!canWrite) return;
    const deletingCurrent = staleBranches.some((b) => b.id === currentBranchId);
    for (const branch of staleBranches) {
      z.mutate(sharedMutators.deleteBranch({ branchId: branch.id }));
    }
    setCleanupModalOpen(false);
    if (deletingCurrent) {
      navigate(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId: mainBranch?.id ?? "",
      });
    }
  };

  const startRename = () => {
    setOpenPopover("none");
    setRenamingBranch({ id: currentBranch.id, name: currentBranch.name });
  };

  const { pushLive, mergeMain } = usePushLive({
    branchId: currentBranch.id,
    spaceId,
    onAccept: () => setOpenPopover("none"),
    onDeploying,
    onMerging,
  });
  const mergeStatus = useMainMergeStatus(currentBranch.id, mainBranch?.id);
  const hasMainConflicts =
    mergeStatus === "conflicts" || mergeStatus === "unresolved";
  const approved = pushLiveRequest.request?.approvedByUserId != null;
  const pushLiveBlocked = !canPushLive || (blockedBySelfMerge && !approved);
  const pushLiveDisabled =
    pushLiveBlocked || isMerging || isDeploying || mergeStatus === "loading";
  if (preflightOpen && pushLiveBlocked) setPreflightOpen(false);
  const currentUserId = useCurrentUserId();
  const isMyRequest =
    pushLiveRequest.request?.requestedByUserId === currentUserId;
  const requestState =
    !(blockedBySelfMerge && canWrite && canPushLive) || approved
      ? null
      : !pushLiveRequest.request
        ? "request"
        : "pending";
  const reviewState =
    blockedBySelfMerge || !pushLiveRequest.request || !canPushLive
      ? null
      : approved
        ? "approved"
        : "review";
  const requestLabel =
    requestState === "pending" ? "Request sent" : "Send push request";
  const requestDisabled =
    requestState === "pending" ||
    pushLiveRequest.submitting ||
    isMerging ||
    isDeploying;
  const openRequestPickerRef = useRef<(() => void) | null>(null);
  const handleRequestPushLive = () => {
    if (!canWrite || !canPushLive || requestDisabled) return;
    setOpenPopover("none");
    openRequestPickerRef.current?.();
  };
  const reviewerNames = (pushLiveRequest.request?.reviewers ?? []).flatMap(
    (r) => (r.user ? [userDisplayName(r.user)] : [])
  );
  const requestedOf =
    reviewerNames.length === 0
      ? "Your peers have been asked"
      : `You asked ${toSentence(
          reviewerNames.length <= 3
            ? reviewerNames
            : [
                ...reviewerNames.slice(0, 2),
                `${reviewerNames.length - 2} others`,
              ]
        )}`;
  const requestExcludedUserIds = new Set(
    [
      currentUserId,
      currentBranch.userId,
      ...currentBranch.contributors.map((c) => c.userId),
    ].flatMap((id) => (id ? [id] : []))
  );
  const requestedBy = pushLiveRequest.request?.requestedByUser
    ? `Requested by ${userDisplayName(pushLiveRequest.request.requestedByUser)}`
    : undefined;
  const approvedBy = pushLiveRequest.request?.approvedByUser
    ? `Approved by ${userDisplayName(pushLiveRequest.request.approvedByUser)}`
    : undefined;
  const handleApprove = () => {
    if (!canPushLive) return;
    setOpenPopover("none");
    void pushLiveRequest.approve();
  };
  // With the markers already in the tree there is no merge left to run.
  const resolveConflictsDisabled =
    !canWrite || isMerging || isDeploying || mergeStatus === "unresolved";
  const handlePushToLive = () => {
    if (pushLiveDisabled) return;
    setOpenPopover("none");
    setPreflightOpen(true);
  };
  const handleResolveConflicts = () => {
    if (resolveConflictsDisabled) return;
    setOpenPopover("none");
    mergeMain();
  };

  const handleSyncGit = () => {
    if (!canSync || isSyncing || connectorMissing) return;
    setOpenPopover("none");
    z.mutate(sharedMutators.syncGit({ spaceId, action: "sync" }));
  };

  const handleSaveToHistory = () => {
    if (!canWrite) return;
    z.mutate(sharedMutators.saveToHistory({ branchId: currentBranch.id }));
    setOpenPopover("none");
  };

  const getHistoryUrl = useCallback(
    (historyCommitId: string) => {
      return makeUrl(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId: currentBranch.id,
        commitId: historyCommitId,
      });
    },
    [currentBranch, spaceId, workflow.id, makeUrl]
  );

  const getHeadUrl = useCallback(() => {
    return makeUrl(routes.space.workflow, {
      spaceId,
      workflowId: workflow.id,
      branchId: currentBranch.id,
      commitId: "",
    });
  }, [currentBranch, spaceId, workflow.id, makeUrl]);

  const handleDownloadChat = () => {
    setOpenPopover("none");
    const toastKey = nanoid(12);
    showToast({
      key: toastKey,
      icon: <Spinner />,
      message: "Preparing download",
      buttonLabel: "Dismiss",
      persist: true,
    });
    downloadChatAsJson(
      chatExportPath("branch", currentBranch.id),
      `${workflow.name}-${currentBranch.name === "main" ? `v${versionNumber}` : currentBranch.name}`
    )
      .catch(() => {
        showToast({
          icon: "circle-off",
          message: "Failed to download chat",
          buttonLabel: "Dismiss",
        });
      })
      .finally(() => hideToast(toastKey));
  };

  const handleDownloadCode = useCallback(() => {
    setDownloadingCode(true);
    setOpenPopover("none");
  }, []);

  const handleForkAsBranch = () => {
    if (!canCreateBranch) return;
    forkAsBranch(currentBranch, commitId);
    setOpenPopover("none");
  };

  const headCommit = currentBranch.commits.find(
    (c) => c.id === currentBranch.headCommitId
  );
  const hasUnsavedChanges = headCommit && !headCommit.message;
  const sortedCheckpoints = [...checkpoints].sort(
    (a, b) => b.createdAt - a.createdAt
  );

  const currentBranchContributors = useMemo(
    () => getContributors(currentBranch),
    [currentBranch]
  );
  const currentTimestamp = getTimestampInfo(currentBranch);

  // Get historical commit info when viewing history
  const historicalCommit = isViewingHistory
    ? currentBranch.commits.find((c) => c.id === commitId)
    : null;
  const historicalVersionIndex = historicalCommit
    ? sortedCheckpoints.findIndex((c) => c.id === historicalCommit.id)
    : -1;
  const historicalVersionNumber =
    historicalVersionIndex >= 0 ? versionNumber - historicalVersionIndex : null;
  const historicalTimestamp = historicalCommit
    ? formatVersionDate(historicalCommit.createdAt)
    : null;

  const currentBranchFacePile = currentBranchContributors.length > 0 && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: 16,
      }}
    >
      <FacePile
        itemSize={16}
        outlineWidth={2}
        maxItems={3}
        cutout
        items={currentBranchContributors.map((user) => {
          const isPresent =
            presentUserBranches.get(user.id)?.has(currentBranchId) ?? false;
          return {
            id: user.id,
            label: userDisplayName(user),
            bare: !isPresent,
            node: (
              <Avatar
                size={16}
                user={user}
                isPresent={isPresent}
                bare={!isPresent}
              />
            ),
          };
        })}
      />
    </div>
  );

  const branchControls = (
    <>
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        isGitSynced &&
        hasChangesToSync && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={
              connectorMissing
                ? GIT_SYNC_PAUSED_MESSAGE
                : canSync
                  ? isSyncing
                    ? "Syncing…"
                    : (syncError ??
                      "Push this draft to the git remote and merge it there")
                  : "You don’t have permission to sync"
            }
          >
            <SyncButton
              disabled={!canSync || isSyncing || connectorMissing}
              onClick={handleSyncGit}
            >
              <RefreshSpin spinning={isSyncing}>
                <Icon name="refresh-cw" size={12} color="white" />
              </RefreshSpin>
              <Text variant="text-small-md" color="white">
                Sync
              </Text>
            </SyncButton>
          </Tooltip>
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        hasMainConflicts && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={
              !canWrite
                ? "You don’t have permission to edit this draft"
                : isMerging
                  ? "Merging live changes…"
                  : mergeStatus === "unresolved"
                    ? "Resolve the conflicts in this draft before pushing live"
                    : "Live has changes that conflict with this draft. Pull them in to resolve."
            }
          >
            <ResolveConflictsButton
              disabled={resolveConflictsDisabled}
              onClick={handleResolveConflicts}
            >
              <Icon name="merge" size={12} color="white" />
              <Text variant="text-small-md" color="white">
                Resolve conflicts
              </Text>
            </ResolveConflictsButton>
          </Tooltip>
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        !hasMainConflicts &&
        requestState === "pending" &&
        isMyRequest && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={`${requestedOf} to review and approve this. Click to cancel. Making more changes also cancels it.`}
          >
            <RequestSentButton onClick={pushLiveRequest.cancel}>
              <ToggleIconContainer aria-hidden>
                <DefaultIcon>
                  <Icon
                    name="heart-handshake"
                    size={12}
                    color="var(--gray-800)"
                  />
                </DefaultIcon>
                <HoverIcon>
                  <Icon name="x" size={12} color="var(--gray-800)" />
                </HoverIcon>
              </ToggleIconContainer>
              <Text variant="text-small-md" color="var(--gray-800)">
                Request sent
              </Text>
            </RequestSentButton>
          </Tooltip>
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        !hasMainConflicts &&
        requestState &&
        !(requestState === "pending" && isMyRequest) && (
          <PushRequestPicker
            spaceId={spaceId}
            workflowId={workflow.id}
            excludedUserIds={requestExcludedUserIds}
            disabled={requestDisabled}
            openRef={openRequestPickerRef}
            onSend={(reviewerIds) => void pushLiveRequest.submit(reviewerIds)}
            opener={
              <Tooltip
                contentStyle={PILL_TOOLTIP_STYLE}
                content={
                  requestState === "pending"
                    ? "Waiting for a peer to approve this"
                    : "This space needs a peer to approve your changes before you push them live"
                }
              >
                <PushRequestPill>
                  <Icon name="heart-handshake" size={12} color="white" />
                  <Text variant="text-small-md" color="white">
                    {requestLabel}
                  </Text>
                </PushRequestPill>
              </Tooltip>
            }
          />
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        !hasMainConflicts &&
        reviewState === "review" && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={`${requestedBy}. Approving lets them push it live.`}
          >
            <PushLiveButton onClick={handleApprove}>
              <Icon name="check" size={12} color="white" />
              <Text variant="text-small-md" color="white">
                Approve
              </Text>
            </PushLiveButton>
          </Tooltip>
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        !hasMainConflicts &&
        reviewState === "approved" && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={`${approvedBy}. Waiting for the author to push it live.`}
          >
            <RequestSentButton disabled>
              <Icon name="check" size={12} color="var(--gray-800)" />
              <Text variant="text-small-md" color="var(--gray-800)">
                Approved
              </Text>
            </RequestSentButton>
          </Tooltip>
        )}
      {!isDeletedOnBranch &&
        hasChangesToPush &&
        !isGitSynced &&
        !hasMainConflicts &&
        !requestState &&
        !reviewState && (
          <Tooltip
            contentStyle={PILL_TOOLTIP_STYLE}
            content={
              !canPushLive
                ? "You don’t have permission to push live"
                : blockedBySelfMerge && !approved
                  ? "This space requires a different user to push branches live"
                  : isMerging
                    ? "Merging live changes…"
                    : isDeploying
                      ? "Deploying…"
                      : (approvedBy ?? "Your changes are ready")
            }
          >
            <PushLiveButton
              disabled={pushLiveDisabled}
              onClick={handlePushToLive}
            >
              <Icon name="rocket" size={12} color="white" />
              <Text variant="text-small-md" color="white">
                Push live
              </Text>
            </PushLiveButton>
          </Tooltip>
        )}
      {branchPr &&
        (() => {
          const isGitlab = branchPr.provider === "gitlab";
          const state = branchPr.state ?? "open";
          const label = isGitlab
            ? `MR !${branchPr.prNumber}`
            : `PR #${branchPr.prNumber}`;
          const kind = isGitlab ? "merge" : "pull";
          return (
            <Tooltip
              contentStyle={PILL_TOOLTIP_STYLE}
              content={`${PR_STATE_LABEL[state]} ${kind} request: ${branchPr.title}`}
            >
              <PrBadge
                href={branchPr.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...PR_STATE_STYLE[state], outline: "none" }}
              >
                <Icon name={PR_STATE_ICON[state]} size={12} />
                <Text variant="text-small-md">{label}</Text>
              </PrBadge>
            </Tooltip>
          );
        })()}
      {!branchPr &&
        isGitSynced &&
        !isMainBranch &&
        // Only once the remote is caught up: with unsynced changes the
        // Sync pill shows instead, and a PR opened now would miss them.
        !hasChangesToSync &&
        prLinkReady &&
        spaceGitBranch &&
        gitRemote?.remoteUrl &&
        (() => {
          if (isSyncing) {
            // A sync is running against an already-synced branch; show
            // its progress in place of the create link until it settles.
            return (
              <SyncingBadge>
                <RefreshSpin spinning>
                  <Icon name="refresh-cw" size={12} />
                </RefreshSpin>
                <Text variant="text-small-md">Syncing…</Text>
              </SyncingBadge>
            );
          }
          return (
            <CreatePullRequest
              key={currentBranch.headCommitId}
              remoteUrl={gitRemote.remoteUrl}
              gitBranchName={spaceGitBranch.name}
              workflowId={workflow.id}
              commitId={currentBranch.headCommitId}
              canGenerate={
                canWrite && currentBranch.headCommitChat?.respondingAt == null
              }
            />
          );
        })()}
      {isMainBranch && isPaused && (
        <Tooltip
          contentStyle={PILL_TOOLTIP_STYLE}
          content={
            canWrite && canPushLive
              ? "Resume schedules and handling web requests"
              : "You don’t have permission to resume"
          }
        >
          <ResumeButton
            disabled={!canWrite || !canPushLive}
            onClick={handleResume}
          >
            <Icon name="play" size={12} color="var(--text-primary)" />
            <Text variant="text-small-md" color="var(--text-primary)">
              Resume
            </Text>
          </ResumeButton>
        </Tooltip>
      )}
    </>
  );
  const actionMenu = (
    <ActionPopover
      isOpen={actionsOpen}
      direction="right"
      popoverRef={popoverRef}
      popoverAnchorName={actionAnchorName}
      trigger={(actionProps) => (
        <SubtleButton
          {...actionProps}
          variant="secondary"
          size="m"
          icon="more"
          aria-label={`${isMainBranch ? getDisplayName(currentBranch) : currentBranch.name} options`}
          aria-expanded={actionsOpen}
          onClick={() => {
            setActionBranchId(currentBranch.id);
            setOpenPopover(actionsOpen ? "none" : "actions");
          }}
        />
      )}
    >
      <>
        {!isMainBranch && isGitSynced && (
          <MenuItem
            disabled={!canSync || isSyncing || connectorMissing}
            onClick={handleSyncGit}
          >
            <RefreshSpin spinning={isSyncing}>
              <Icon name="refresh-cw" size={16} color="var(--text-primary)" />
            </RefreshSpin>
            <Text variant="text-regular" color="var(--text-primary)">
              {isSyncing ? "Syncing…" : "Sync to git"}
            </Text>
          </MenuItem>
        )}
        {!isMainBranch && !isGitSynced && hasMainConflicts && (
          <MenuItem
            disabled={resolveConflictsDisabled}
            onClick={handleResolveConflicts}
          >
            <Icon name="merge" size={16} color="var(--text-primary)" />
            <Text variant="text-regular" color="var(--text-primary)">
              Resolve conflicts
            </Text>
          </MenuItem>
        )}
        {!isMainBranch &&
          !isDeletedOnBranch &&
          hasChangesToPush &&
          !isGitSynced &&
          !hasMainConflicts &&
          requestState && (
            <MenuItem
              disabled={requestDisabled}
              onClick={handleRequestPushLive}
            >
              <Icon
                name="heart-handshake"
                size={16}
                color="var(--text-primary)"
              />
              <Text variant="text-regular" color="var(--text-primary)">
                {requestLabel}
              </Text>
            </MenuItem>
          )}
        {isMyRequest && (
          <MenuItem
            disabled={!canWrite}
            onClick={() => {
              if (!canWrite) return;
              setOpenPopover("none");
              pushLiveRequest.cancel();
            }}
          >
            <Icon name="x" size={16} color="var(--text-primary)" />
            <Text variant="text-regular" color="var(--text-primary)">
              Cancel push request
            </Text>
          </MenuItem>
        )}
        {!isMainBranch && !isGitSynced && !hasMainConflicts && reviewState && (
          <MenuItem
            disabled={reviewState === "approved"}
            title={reviewState === "approved" ? approvedBy : requestedBy}
            onClick={handleApprove}
          >
            <Icon name="check" size={16} color="var(--text-primary)" />
            <Text variant="text-regular" color="var(--text-primary)">
              {reviewState === "approved" ? "Approved" : "Approve"}
            </Text>
          </MenuItem>
        )}
        {!isMainBranch &&
          !isGitSynced &&
          !hasMainConflicts &&
          !requestState &&
          !reviewState && (
            <MenuItem
              disabled={pushLiveDisabled}
              title={
                blockedBySelfMerge && !approved
                  ? "This space requires a different user to push branches live"
                  : approvedBy
              }
              onClick={handlePushToLive}
            >
              <Icon name="rocket" size={16} color="var(--text-primary)" />
              <Text variant="text-regular" color="var(--text-primary)">
                Push live
              </Text>
            </MenuItem>
          )}
        {isMainBranch && !isPaused && (
          <MenuItem
            disabled={!canWrite || !canPushLive}
            onClick={() => {
              setOpenPopover("none");
              setPauseConfirmOpen(true);
            }}
          >
            <Icon name="circle-pause" size={16} color="var(--text-primary)" />
            <Text variant="text-regular" color="var(--text-primary)">
              Pause workflow
            </Text>
          </MenuItem>
        )}
        {isMainBranch && isPaused && (
          <MenuItem disabled={!canWrite || !canPushLive} onClick={handleResume}>
            <Icon name="play" size={16} color="var(--text-primary)" />
            <Text variant="text-regular" color="var(--text-primary)">
              Resume workflow
            </Text>
          </MenuItem>
        )}
        <MenuItem
          disabled={
            !canWrite ||
            isDeletedOnBranch ||
            !changeReview.canGenerate ||
            currentBranch.headCommitChat?.respondingAt != null
          }
          onClick={() => {
            setOpenPopover("none");
            void changeReview.generate();
          }}
        >
          <Icon name="diffs" size={16} color="var(--text-primary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            {changeReview.generating
              ? "Highlighting changes…"
              : "Highlight changes"}
          </Text>
        </MenuItem>
        <MenuItem onClick={() => setOpenPopover("dropdown")}>
          <Icon name="history" size={16} color="var(--text-primary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            History
          </Text>
        </MenuItem>
        <MenuItem onClick={handleDownloadChat}>
          <Icon name="message-circle" size={16} color="var(--text-primary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            Download chat
          </Text>
        </MenuItem>
        <MenuItem onClick={handleDownloadCode}>
          <Icon name="square-code" size={16} color="var(--text-primary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            Download code
          </Text>
        </MenuItem>
        <MenuItem disabled={!canCreateBranch} onClick={handleForkAsBranch}>
          <Icon name="git-branch" size={16} color="var(--text-primary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            Fork as branch
          </Text>
        </MenuItem>
        {!isMainBranch && (
          <>
            <MenuItem disabled={!canWrite} onClick={startRename}>
              <Icon name="pencil" size={16} color="var(--text-primary)" />
              <Text variant="text-regular" color="var(--text-primary)">
                Rename branch
              </Text>
            </MenuItem>
            <MenuDivider />
            <MenuItem disabled={!canWrite} onClick={handleDeleteBranch}>
              <Icon name="trash" size={16} color="var(--text-primary)" />
              <Text variant="text-regular" color="var(--text-primary)">
                Delete branch
              </Text>
            </MenuItem>
          </>
        )}
      </>
    </ActionPopover>
  );

  const tidyUpAction =
    staleBranches.length > 0 ? (
      <Tooltip content="Tidy up branches">
        <TidyButton
          aria-label="Tidy up branches"
          disabled={!canWrite}
          onPress={() => {
            if (!canWrite) return;
            setOpenPopover("none");
            setCleanupModalOpen(true);
          }}
        >
          <Icon name="brush-cleaning" size={12} />
        </TidyButton>
      </Tooltip>
    ) : null;

  const versionActions = (
    <div
      style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
    >
      {tidyUpAction}
      <CreateBranchDropdown
        currentBranch={currentBranch}
        liveBranch={mainBranch}
        canWrite={canCreateBranch}
        onFork={(branch) => {
          if (!canCreateBranch) return;
          forkAsBranch(branch, null);
          setOpenPopover("none");
        }}
      />
    </div>
  );

  const bannerHistory = !!children;
  const dropdown = (
    <DropdownPopover
      isOpen={openPopover === "dropdown"}
      direction={bannerHistory ? "down" : "right"}
      style={
        !bannerHistory
          ? {
              left: "anchor(right)",
              right: "auto",
              top: "anchor(top)",
              marginTop: 0,
              marginLeft: 8,
              positionTryFallbacks:
                "flip-inline, flip-block, flip-inline flip-block, --dropdown-fit-viewport, --dropdown-fit-viewport flip-block",
              width: "min(400px, calc(100vw - 32px))",
            }
          : { borderRadius: 8 }
      }
      popoverRef={popoverRef}
      target={bannerHistory ? bannerAnchorName : navigationAnchorName}
      popoverAnchorName={popoverAnchorName}
      trigger={() => null}
    >
      <DropdownHeader>
        <Text variant="text-small" color="var(--text-secondary)">
          {isMainBranch ? "Version history" : "Branch history"}
        </Text>
      </DropdownHeader>
      {hasUnsavedChanges &&
        (isViewingHistory ? (
          <VersionRowLink
            to={getHeadUrl()}
            onMouseDown={() => setOpenPopover("none")}
          >
            <Icon name="pencil" size={16} color="var(--text-primary)" />
            <VersionRowContent>
              <Text variant="text-regular" ellipsis>
                Unsaved changes
              </Text>
            </VersionRowContent>
          </VersionRowLink>
        ) : (
          <VersionRow
            disabled={!canWrite}
            onClick={() => {
              if (!canWrite) return;
              playClick();
              handleSaveToHistory();
            }}
          >
            <Icon name="save" size={16} color="var(--text-primary)" />
            <VersionRowContent>
              <Text variant="text-regular" ellipsis>
                Save to history
              </Text>
            </VersionRowContent>
          </VersionRow>
        ))}
      {sortedCheckpoints.length === 0 && !hasUnsavedChanges ? (
        <VersionRow data-selected="true">
          <Text variant="text-small" color="var(--text-secondary)">
            No history yet
          </Text>
        </VersionRow>
      ) : (
        sortedCheckpoints.map((checkpoint, index) => {
          const isCurrentVersion = checkpoint.id === commitId;
          const isHead =
            !isViewingHistory && checkpoint.id === currentBranch.headCommitId;
          const version = versionNumber - index;
          const { date } = formatVersionDate(checkpoint.createdAt);
          const isViewing = isCurrentVersion || isHead;
          const historyUrl = getHistoryUrl(checkpoint.id);

          if (isViewing) {
            return (
              <VersionRow key={checkpoint.id} data-selected="true">
                <Icon name="history" size={16} color="var(--text-primary)" />
                <VersionRowContent>
                  <Text variant="text-regular" ellipsis>
                    {isMainBranch
                      ? checkpoint.message
                        ? `v${version}${META_SEP}${checkpoint.message}`
                        : `v${version}`
                      : checkpoint.message || `Version ${version}`}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {isMainBranch ? "pushed live" : "saved"} {date}
                  </Text>
                  <ViewingBadge>Viewing</ViewingBadge>
                </VersionRowContent>
              </VersionRow>
            );
          }

          return (
            <VersionRowLink
              key={checkpoint.id}
              to={historyUrl}
              onMouseDown={() => setOpenPopover("none")}
            >
              <Icon name="history" size={16} color="var(--text-primary)" />
              <VersionRowContent>
                <Text variant="text-regular" ellipsis>
                  {isMainBranch
                    ? checkpoint.message
                      ? `v${version}${META_SEP}${checkpoint.message}`
                      : `v${version}`
                    : checkpoint.message || `Version ${version}`}
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  {isMainBranch ? "pushed live" : "saved"} {date}
                </Text>
              </VersionRowContent>
            </VersionRowLink>
          );
        })
      )}
    </DropdownPopover>
  );

  const selector = (
    <Wrapper ref={containerRef} onKeyDown={handlePopoverKeyDown} tabIndex={-1}>
      <Heading style={{ minHeight: 44, marginTop: 8, marginBottom: 2 }}>
        <Text variant="text-small">Versions and branches</Text>
        {versionActions}
      </Heading>
      {sortedBranches.map((branch, index) => {
        const selected = branch.id === currentBranchId;
        const main = branch.name === "main";
        const role = branchRole({ id: branch.id, workflow });
        const timestamp = getTimestampInfo(branch);
        const responding = !selected && !!branch.headCommitChat?.respondingAt;
        const requestDot = pushRequestDotColor(branch);
        const metadata = main
          ? isPaused && workflow.pausedAt != null
            ? `paused ${formatVersionDate(workflow.pausedAt).date}`
            : `${timestamp.prefix} ${timestamp.date}`
          : undefined;
        return (
          <NavigationTreeRow
            key={branch.id}
            nested={!main && !!mainBranch}
            last={index === sortedBranches.length - 1}
          >
            <BranchNavigationRow
              data-selected={selected || undefined}
              data-menu-open={(selected && actionsOpen) || undefined}
              data-status={responding || !!requestDot || undefined}
              style={
                selected ? { anchorName: navigationAnchorName } : undefined
              }
            >
              <StyledLink
                to={getBranchUrl(branch.id)}
                aria-current={
                  selected && !isViewingHistory ? "page" : undefined
                }
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
                onMouseDown={() => setOpenPopover("none")}
                onClick={() => setOpenPopover("none")}
              >
                {main ? (
                  <LiveIndicator paused={isPaused} />
                ) : (
                  <Icon name={BRANCH_ROLE_ICON[role]} size={16} />
                )}
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Text
                    ellipsis={!main}
                    style={main ? { flexShrink: 0 } : { flex: 1 }}
                  >
                    {getDisplayName(branch)}
                  </Text>
                  {metadata && (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {metadata}
                    </Text>
                  )}
                </span>
                {(responding || requestDot) && (
                  <NavigationStatus aria-hidden="true" data-hide-on-hover>
                    {responding ? (
                      <StatusIndicator
                        state="building"
                        focused={false}
                        size={12}
                      />
                    ) : (
                      <BadgeDot
                        style={{ background: requestDot ?? undefined }}
                      />
                    )}
                  </NavigationStatus>
                )}
              </StyledLink>
              <div
                data-space-action
                style={{
                  position: "absolute",
                  right: 4,
                }}
              >
                {selected ? (
                  actionMenu
                ) : (
                  <SubtleButtonLink
                    to={getBranchUrl(branch.id)}
                    variant="secondary"
                    size="m"
                    icon="more"
                    aria-label={`${main ? getDisplayName(branch) : branch.name} options`}
                    aria-haspopup="menu"
                    onPointerDown={(event) => {
                      if (!isInAppNavigation(event)) return;
                      if (chatBanner) actionsOnArrival.branchId = branch.id;
                      setActionBranchId(branch.id);
                      setOpenPopover("actions");
                    }}
                    onClick={(event) => {
                      if (!isInAppNavigation(event) || event.detail !== 0)
                        return;
                      if (chatBanner) actionsOnArrival.branchId = branch.id;
                      setActionBranchId(branch.id);
                      setOpenPopover("actions");
                    }}
                  />
                )}
              </div>
            </BranchNavigationRow>
          </NavigationTreeRow>
        );
      })}
      {!bannerHistory && dropdown}
    </Wrapper>
  );

  const bannerName = isMainBranch
    ? `V${historicalVersionNumber ?? versionNumber}`
    : currentBranch.name;
  const bannerIcon = isMainBranch ? (
    isViewingHistory ? (
      <HistoryIndicator />
    ) : (
      <LiveIndicator paused={isPaused} />
    )
  ) : (
    BRANCH_ROLE_ICON[currentBranchRole]
  );
  const bannerMetadata = isDeploying
    ? "Deploying…"
    : isMerging
      ? "Merging live changes…"
      : isMainBranch && historicalTimestamp
        ? `pushed live ${historicalTimestamp.date}`
        : isMainBranch && isPaused && workflow.pausedAt != null
          ? `paused ${formatVersionDate(workflow.pausedAt).date}`
          : `${currentTimestamp.prefix} ${currentTimestamp.date}`;
  const bannerAvatar =
    currentBranchFacePile ||
    (currentBranch.user && (
      <Avatar
        user={currentBranch.user}
        size={16}
        isPresent={
          presentUserBranches
            .get(currentBranch.user.id)
            ?.has(currentBranchId) ?? false
        }
      />
    ));
  const bannerHistoryToggle = {
    open: openPopover === "dropdown",
    onToggle: () =>
      setOpenPopover(openPopover === "dropdown" ? "none" : "dropdown"),
  };
  const banner = (
    <BranchBanner
      ref={setBannerElement}
      anchorName={bannerAnchorName}
      onKeyDown={handlePopoverKeyDown}
      history={{
        ...bannerHistoryToggle,
        menu: bannerHistory ? dropdown : null,
      }}
      name={bannerName}
      icon={bannerIcon}
      metadata={bannerMetadata}
      avatar={bannerAvatar}
      actions={branchControls}
    />
  );
  const chatBannerParts: ChatBannerParts = {
    icon:
      typeof bannerIcon === "string" ? (
        <Icon name={bannerIcon} size={16} />
      ) : (
        bannerIcon
      ),
    name: bannerName,
    metadata: bannerMetadata,
    controls: branchControls,
    avatar: bannerAvatar,
    historyMenu: bannerHistory ? dropdown : null,
    history: bannerHistoryToggle,
    anchor: {
      ref: setBannerElement,
      anchorName: bannerAnchorName,
      onKeyDown: handlePopoverKeyDown,
    },
  };

  return (
    <>
      {children ? children(selector, banner, chatBannerParts) : selector}
      {renamingBranch && canWrite && (
        <RenameModal
          title="Rename branch"
          inputLabel="Branch name"
          initialName={renamingBranch.name}
          validateName={branchNameError}
          onClose={() => setRenamingBranch(null)}
          onRename={(name) => {
            z.mutate(
              sharedMutators.renameBranch({ branchId: renamingBranch.id, name })
            );
          }}
        />
      )}
      {deleteLastBranch.modal}
      <TidyUpModal
        open={cleanupModalOpen && canWrite}
        onClose={() => setCleanupModalOpen(false)}
        onConfirm={handleTidyUp}
        itemNoun={{ singular: "branch", plural: "branches" }}
        inactivityDays={BRANCH_INACTIVITY_DAYS}
        items={staleBranches.map((branch) => {
          const role = branchRole({ id: branch.id, workflow });
          return {
            key: branch.id,
            href: makeUrl(routes.space.workflow, {
              spaceId,
              workflowId: workflow.id,
              branchId: branch.id,
            }),
            name: branch.name,
            badge: (
              <Icon
                name={BRANCH_ROLE_ICON[role]}
                size={16}
                color="var(--text-primary)"
              />
            ),
          };
        })}
      />
      <ConfirmModal
        open={pauseConfirmOpen && canWrite && canPushLive}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={handleConfirmPause}
        title="Pause workflow?"
        description="Schedules will stop running, incoming web requests will be rejected, and no manual runs can start. You can resume at any time."
        confirmLabel="Pause"
      />
      {preflightOpen && !pushLiveBlocked && (
        <PushLivePreflight
          workflowId={workflow.id}
          branchId={currentBranch.id}
          spaceId={spaceId}
          onProceed={(copyVolumesToLive) => {
            if (pushLiveBlocked) return;
            setPreflightOpen(false);
            void pushLive(currentBranch, mainBranch?.id, copyVolumesToLive);
          }}
          onCancel={() => setPreflightOpen(false)}
          onInvestigateFailures={onInvestigateFailures}
        />
      )}
    </>
  );
}
